import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Icon } from '~/components';
import { colors } from '~/styles';
import MessageContent from './MessageContent';
import SpanBlock from './SpanBlock';

const Turn = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 1rem;
`;

const UserMessage = styled.div`
  align-self: flex-start;
  background: ${colors.primary10};
  color: ${colors.black};
  padding: 1.5rem 1.25rem;
  border-radius: 0.625rem 0.625rem 0.625rem 0;
  white-space: pre-wrap;
  line-height: 1.5;
  font-size: 0.875rem;
  display: inline-block;
`;

const AssistantMessage = styled.div`
  align-self: flex-start;
  background: ${colors.grey5};
  padding: 1.5rem 1.25rem;
  border-radius: 0.625rem 0.625rem 0.625rem 0;
  color: ${colors.black};
  overflow: hidden;
  line-height: 1.5;
  font-size: 0.875rem;
  display: inline-block;

  h1,
  h2,
  h3 {
    font-weight: 600;
    color: ${colors.black};
    margin: 1rem 0 0.5rem;
  }

  h1 {
    font-size: 1.125rem;
  }

  h2 {
    font-size: 1rem;
  }

  h3 {
    font-size: 0.875rem;
  }

  p {
    white-space: pre-wrap;
    margin-bottom: 0.5rem;
  }

  ol {
    margin-bottom: 0.5rem;
    padding-left: 1rem;
    list-style-position: inside;
  }

  ul {
    margin-bottom: 0.5rem;
    list-style-position: inside;
    list-style-type: disc;
    padding-left: 1rem;
  }
`;

const MessageGroup = styled.div`
  width: 100%;
`;

const Timestamp = styled.div`
  font-size: 0.6875rem;
  color: ${colors.grey40};
  margin-bottom: 0.25rem;
`;

const SpansContainer = styled.div`
  padding-left: 1rem;
  border-left: solid 2px ${colors.grey10};
  width: 100%;
`;

const FeedbackList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.375rem;
`;

const FeedbackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: ${colors.grey40};
`;

const FeedbackIcon = styled.span`
  color: ${({ $color }) => $color};
  font-size: 0.6875rem;
  display: flex;
  align-items: center;
`;

function feedbackLabel(value) {
  if (value === 1) return { icon: 'thumbs-up', label: 'Liked', color: colors.primary };
  if (value === -1) return { icon: 'thumbs-down', label: 'Disliked', color: colors.danger };
  return { icon: 'minus-circle', label: 'Removed rating', color: colors.grey40 };
}

function getTitleText(turn) {
  return turn.spans[0]?.response?.text?.trim() || null;
}

export default function TurnBlock({ turn }) {
  if (turn.traceType === 'title') {
    const span = turn.spans[0];
    const generatedTitle = getTitleText(turn);
    return (
      <Turn>
        <MessageGroup>
          <Timestamp>{moment(turn.timestamp).format('MMM D, YYYY h:mm:ss A')} · Title generation</Timestamp>
          {span && (
            <SpansContainer>
              <SpanBlock span={span} index={0} />
            </SpansContainer>
          )}
          {generatedTitle && <AssistantMessage>{generatedTitle}</AssistantMessage>}
        </MessageGroup>
      </Turn>
    );
  }

  const userParts = turn.userMessage?.eventData?.parts || [];
  const userText = userParts.map((p) => p.text || '').join('');
  const assistantParts = turn.assistantMessage?.eventData?.parts || [];
  const toolParts = assistantParts.filter((p) => p.type === 'tools');
  const contentParts = assistantParts.filter((p) => p.type !== 'tools');

  return (
    <Turn>
      {turn.userMessage && (
        <MessageGroup>
          <Timestamp>{moment(turn.userMessage.timestamp).format('MMM D, YYYY h:mm:ss A')} · User</Timestamp>
          <UserMessage>{userText}</UserMessage>
        </MessageGroup>
      )}

      {turn.spans.length > 0 && (
        <MessageGroup>
          <Timestamp>
            {moment(turn.spans[0].earliestTimestamp).format('MMM D, YYYY h:mm:ss A')} · {turn.spans.length} LLM step
            {turn.spans.length !== 1 ? 's' : ''}
          </Timestamp>
          <SpansContainer>
            {turn.spans.map((span, i) => (
              <SpanBlock key={span.spanId} span={span} index={i} />
            ))}
          </SpansContainer>
        </MessageGroup>
      )}

      {toolParts.length > 0 && <MessageContent parts={toolParts} />}

      {(contentParts.length > 0 || turn.feedbackEvents?.length > 0) && (
        <MessageGroup>
          {contentParts.length > 0 && (
            <>
              <Timestamp>
                {turn.assistantMessage?.timestamp &&
                  `${moment(turn.assistantMessage.timestamp).format('MMM D, YYYY h:mm:ss A')} · Assistant`}
              </Timestamp>
              <AssistantMessage>
                <MessageContent parts={contentParts} />
              </AssistantMessage>
            </>
          )}
          {turn.feedbackEvents?.length > 0 && (
            <FeedbackList>
              {turn.feedbackEvents.map((event, i) => {
                const { icon, label, color } = feedbackLabel(event.feedback);
                return (
                  <FeedbackRow key={i}>
                    <FeedbackIcon $color={color}>
                      <Icon icon={icon} />
                    </FeedbackIcon>
                    {label} · {moment(event.timestamp).format('MMM D, YYYY h:mm:ss A')}
                  </FeedbackRow>
                );
              })}
            </FeedbackList>
          )}
        </MessageGroup>
      )}
    </Turn>
  );
}
